import { VOCABULARY_LOOKUP_TIMEOUT } from '@/assets/constants/constants';

/**
 * Service for managing vocabulary lookups with timeout handling
 */
class VocabularyService {
  constructor() {
    this.timeoutId = null;
  }

  /**
   * Creates a promise that rejects after specified timeout
   * @param {number} timeoutMs - Timeout duration in milliseconds
   * @returns {Promise} Promise that rejects on timeout
   */
  createTimeoutPromise(timeoutMs) {
    return new Promise((_, reject) => {
      this.timeoutId = setTimeout(() => {
        reject(new Error('Vocabulary lookup timeout - no results received'));
      }, timeoutMs);
    });
  }

  /**
   * Creates a promise that resolves when vocabulary is received
   * @param {Function} onVocabularyReceived - Callback when vocabulary arrives
   * @returns {Promise} Promise that resolves when vocabulary is received
   */
  createVocabularyPromise(onVocabularyReceived) {
    return new Promise((resolve) => {
      this.onVocabularyReceived = (vocabulary) => {
        this.clearTimeout();
        resolve(vocabulary);
        if (onVocabularyReceived) onVocabularyReceived(vocabulary);
      };
    });
  }

  /**
   * Executes vocabulary lookup with timeout
   * @param {Function} sendRequest - Function that sends the lookup request
   * @param {Function} onVocabularyReceived - Callback when vocabulary arrives
   * @param {number} timeoutMs - Timeout duration (defaults to VOCABULARY_LOOKUP_TIMEOUT)
   * @returns {Promise} Promise that resolves or rejects
   */
  async executeVocabularyLookup(
    sendRequest,
    onVocabularyReceived,
    timeoutMs = VOCABULARY_LOOKUP_TIMEOUT
  ) {
    // Create a promise for vocabulary arrival
    const vocabularyPromise = this.createVocabularyPromise(onVocabularyReceived);
    
    // Create a promise that rejects on timeout
    const timeoutPromise = this.createTimeoutPromise(timeoutMs);

    // Send the request
    sendRequest();

    // Race between vocabulary arrival and timeout
    try {
      return await Promise.race([vocabularyPromise, timeoutPromise]);
    } catch (error) {
      this.clearTimeout();
      throw error;
    }
  }

  /**
   * Notifies service that vocabulary has been received
   * @param {*} vocabulary - The vocabulary data received
   */
  notifyVocabularyReceived(vocabulary) {
    if (this.onVocabularyReceived) {
      this.onVocabularyReceived(vocabulary);
    }
  }

  /**
   * Clears any pending timeout
   */
  clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Cancels the current vocabulary lookup
   */
  cancelLookup() {
    this.clearTimeout();
    this.onVocabularyReceived = null;
  }
}

export default new VocabularyService();
